
import { Options, Vue } from "vue-class-component";
import moment from "moment";
import { DutyTaxConfigurationController } from "@/app/ui/controllers/DutyTaxConfigurationController";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { PaginationV2 } from "@/domain/entities/Pagination";
import {
  DUTY_TAX_CONFIGURATION_DEFAULT_SORT_BY,
  DutyTaxConfigurationListItem,
  DutyTaxConfigurationSortBy,
  DutyTaxConfigurationStatus
} from "@/domain/entities/DutyTaxConfiguration";

class DutyTaxConfigurationListData {
  pagination = new PaginationV2({ limit: 20 });
  data: DutyTaxConfigurationListItem[] = [];

  constructor(fields?: Partial<DutyTaxConfigurationListData>) {
    Object.assign(this, fields);
  }
}

@Options({})
export default class DutyTaxConfigurationList extends Vue {
  isLoading = false;
  errorCause = "";
  listData: DutyTaxConfigurationListData = new DutyTaxConfigurationListData();
  listLoaded = false;

  draftCountry: { name: string; value: string } | "" = "";
  draftStatus: DutyTaxConfigurationStatus | "" = "";
  appliedCountryCode = "";
  appliedStatus: DutyTaxConfigurationStatus | "" = "";
  statusTitle = "";
  isStatusOpen = false;

  searchValue = "";
  appliedSearch = "";

  sortBy: DutyTaxConfigurationSortBy = DUTY_TAX_CONFIGURATION_DEFAULT_SORT_BY;
  sortDirectionUi: "ASC" | "DESC" | "" = "ASC";

  statusOptions = [
    { name: "Semua status", value: "" },
    { name: "Aktif", value: "active" },
    { name: "Non-aktif", value: "inactive" }
  ];

  mounted(): void {
    this.fetchCountryOptions("");
    this.fetchList();
  }

  get title(): string {
    return (this.$route as any)?.meta?.title ?? "Konfigurasi Bea Cukai & Pajak";
  }

  get isEmpty(): boolean {
    return this.listLoaded && !this.isLoading && !this.listData.data.length;
  }

  get isLoadingCountry(): boolean {
    return LocationController.isLoadingCountry;
  }

  fetchCountryOptions(search: string): void {
    if (search.length > 1 || !search) {
      LocationController.getCountryList({
        search,
        status: "active",
        page: 1,
        limit: 10
      });
    }
  }

  get countryOptions(): { name: string; value: string }[] {
    return LocationController.countryData.countryData.map((country) => ({
      name: `${country.code} - ${country.name}`,
      value: country.code
    }));
  }

  async fetchList(): Promise<void> {
    this.isLoading = true;
    try {
      const result = await DutyTaxConfigurationController.getDutyTaxConfigurationList(
        {
          page: this.listData.pagination.page,
          limit: this.listData.pagination.limit,
          destinationCountryCode: this.appliedCountryCode,
          status: this.appliedStatus,
          search: this.appliedSearch,
          sortBy: this.sortBy,
          sortDir: this.sortDirectionUi === "DESC" ? "desc" : "asc"
        }
      );
      this.listData = new DutyTaxConfigurationListData({
        pagination: new PaginationV2({
          page: result.pagination.page,
          limit: result.pagination.limit,
          totalData: result.pagination.totalData
        }),
        data: result.data
      });
      this.errorCause = "";
    } catch (error) {
      this.errorCause = parsingErrorResponse(error).type;
    } finally {
      this.isLoading = false;
      this.listLoaded = true;
    }
  }

  resetSort(): void {
    this.sortBy = DUTY_TAX_CONFIGURATION_DEFAULT_SORT_BY;
    this.sortDirectionUi = "ASC";
  }

  resetToFirstPage(): void {
    this.listData.pagination = new PaginationV2({
      page: 1,
      limit: this.listData.pagination.limit
    });
  }

  onApplyFilter(): void {
    this.appliedCountryCode = this.draftCountry ? this.draftCountry.value : "";
    this.appliedStatus = this.draftStatus;
    const trimmed = this.searchValue.trim();
    this.appliedSearch = trimmed.length >= 2 ? trimmed : "";
    this.resetSort();
    this.resetToFirstPage();
    this.fetchList();
  }

  onSearchInput(value: string): void {
    this.searchValue = value;
  }

  onSearchTriggered(event?: KeyboardEvent): void {
    const inputValue =
      (event?.target as HTMLInputElement | null)?.value ?? this.searchValue;
    this.searchValue = inputValue;
    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      if (trimmed.length === 0 && this.appliedSearch) {
        this.appliedSearch = "";
        this.resetToFirstPage();
        this.fetchList();
      }
      return;
    }
    this.appliedSearch = trimmed;
    this.resetSort();
    this.resetToFirstPage();
    this.fetchList();
  }

  onClearSearch(): void {
    const shouldFetch = !!this.appliedSearch;
    this.searchValue = "";
    this.appliedSearch = "";
    if (shouldFetch) {
      this.resetToFirstPage();
      this.fetchList();
    }
  }

  onSort(payload: { sortBy: string; sortDirection: string }): void {
    if (!payload.sortDirection) {
      this.resetSort();
    } else {
      this.sortBy = payload.sortBy as DutyTaxConfigurationSortBy;
      this.sortDirectionUi = payload.sortDirection as "ASC" | "DESC";
    }
    this.resetToFirstPage();
    this.fetchList();
  }

  onOpenStatus(): void {
    this.isStatusOpen = true;
  }

  onCloseStatus(): void {
    this.isStatusOpen = false;
  }

  onSelectStatus(name: string, value: string): void {
    this.draftStatus = (value || "") as DutyTaxConfigurationStatus | "";
    this.statusTitle = name;
  }

  formatWeight(value: number): string {
    return `${Number(value ?? 0).toFixed(2)} kg`;
  }

  formatCurrency(currencyCode: string, value: number): string {
    const formatted = Number(value ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currencyCode || "-"} ${formatted}`;
  }

  formatYesNo(value: boolean): string {
    return value ? "Ya" : "Tidak";
  }

  formatDate(value: string): string {
    return value ? moment(value).format("DD MMM YYYY HH:mm") : "-";
  }

  formatStatus(status: DutyTaxConfigurationStatus): string {
    const isActive = status === "active";
    const pillClass = isActive
      ? "bg-green-lp-300 text-green-lp-400"
      : "bg-red-lp-1300 text-red-lp-2400";
    const label = isActive ? "Aktif" : "Non-aktif";
    return `<span class="inline-flex items-center rounded-full px-2 py-0.5 font-medium ${pillClass}">${label}</span>`;
  }

  get columns() {
    return [
      {
        name: "No.",
        sortBy: "",
        styleHead: "w-14 text-left",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800">${item.no}</div>`
      },
      {
        name: "Negara Tujuan",
        sortBy: "destination_country_code",
        styleHead: "w-56 text-left whitespace-nowrap",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-black-lp-100 font-semibold whitespace-nowrap">${item.destinationCountryCode} - ${item.destinationCountryName}</div>`
      },
      {
        name: "Komoditas",
        sortBy: "commodity_code",
        styleHead: "w-44 text-left whitespace-nowrap",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-black-lp-100 font-semibold break-words">${
            item.commodityCode === "ALL"
              ? "Semua Komoditas"
              : item.commodityName
          }</div>`
      },
      {
        name: "Nilai<br>deminimis",
        sortBy: "deminimis_value",
        styleHead: "w-40 text-left",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800 whitespace-nowrap">${this.formatCurrency(
            item.destinationCurrencyCode,
            item.deminimisValue
          )}</div>`
      },
      {
        name: "Berat<br>min",
        sortBy: "min_weight_kg",
        styleHead: "w-28 text-left",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800 whitespace-nowrap">${this.formatWeight(
            item.minWeightKg
          )}</div>`
      },
      {
        name: "Berat<br>maks",
        sortBy: "max_weight_kg",
        styleHead: "w-28 text-left",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800 whitespace-nowrap">${this.formatWeight(
            item.maxWeightKg
          )}</div>`
      },
      {
        name: "Dokumen<br>edaran",
        sortBy: "",
        styleHead: "w-32 text-left",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800">${this.formatYesNo(
            item.documentCircularRequired
          )}</div>`
      },
      {
        name: "Self-clearance",
        sortBy: "",
        styleHead: "w-32 text-left",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800">${this.formatYesNo(
            item.selfClearanceRequired
          )}</div>`
      },
      {
        name: "Status",
        sortBy: "status",
        styleHead: "w-28 text-left",
        render: (item: DutyTaxConfigurationListItem) =>
          this.formatStatus(item.status)
      },
      {
        name: "Dibuat Oleh",
        sortBy: "",
        styleHead: "w-36 text-left whitespace-nowrap",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800 whitespace-nowrap">${item.createdBy ||
            "-"}</div>`
      },
      {
        name: "Tanggal Dibuat",
        sortBy: "created_at",
        styleHead: "w-44 text-left whitespace-nowrap",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-black-lp-100 font-semibold whitespace-nowrap">${this.formatDate(
            item.createdAt
          )}</div>`
      },
      {
        name: "Diperbarui Oleh",
        sortBy: "",
        styleHead: "w-40 text-left whitespace-nowrap",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-gray-lp-800 whitespace-nowrap">${item.updatedBy ||
            "-"}</div>`
      },
      {
        name: "Tanggal Diperbarui",
        sortBy: "updated_at",
        styleHead: "w-44 text-left whitespace-nowrap",
        render: (item: DutyTaxConfigurationListItem) =>
          `<div class="text-left text-black-lp-100 font-semibold whitespace-nowrap">${this.formatDate(
            item.updatedAt
          )}</div>`
      }
    ];
  }
}
